/**
 * AI Form Generation Service Layer
 * 
 * Production-ready request management with:
 * - Per-user rate limiting (1 request / 2s)
 * - Request deduplication & queueing
 * - Client-side prompt caching (5 min TTL)
 * - Exponential backoff retry (1s → 2s → 4s, max 3)
 * - Debounce helper
 */

import { supabase } from '@/integrations/supabase/client';

// ── Types ──────────────────────────────────────────────────────────────
export interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  placeholder: string;
  options: string[];
}

export interface GeneratedForm {
  title: string;
  description: string;
  fields: FormField[];
  whatsapp_number?: string;
}

export type GenerationStatus =
  | 'idle'
  | 'queued'
  | 'generating'
  | 'adapting'
  | 'creating'
  | 'done'
  | 'error';

interface CacheEntry {
  data: GeneratedForm;
  expiresAt: number;
}

interface QueueItem {
  intent: string;
  currentForm?: GeneratedForm;
  resolve: (v: GeneratedForm) => void;
  reject: (e: Error) => void;
}

// ── Constants ──────────────────────────────────────────────────────────
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MIN_REQUEST_INTERVAL_MS = 2000; // 2 seconds between requests
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY_MS = 1000;

// ── State ──────────────────────────────────────────────────────────────
const promptCache = new Map<string, CacheEntry>();
const requestQueue: QueueItem[] = [];
let isProcessing = false;
let lastRequestTime = 0;
let activeAbortController: AbortController | null = null;
const inFlightRequests = new Map<string, Promise<GeneratedForm>>();

let currentSessionId: string | null = null;

function normalizePrompt(intent: string): string {
  return intent.trim().toLowerCase().replace(/\s+/g, ' ');
}

function getCached(intent: string): GeneratedForm | null {
  const key = normalizePrompt(intent);
  const entry = promptCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    promptCache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(intent: string, data: GeneratedForm) {
  const key = normalizePrompt(intent);
  promptCache.set(key, { data, expiresAt: Date.now() + CACHE_TTL_MS });

  // Evict old entries (keep max 50)
  if (promptCache.size > 50) {
    const oldest = promptCache.keys().next().value;
    if (oldest) promptCache.delete(oldest);
  }
}

async function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function getOrGenerateSessionId(): string {
  if (!currentSessionId) {
    currentSessionId = crypto.randomUUID();
  }
  return currentSessionId;
}

/**
 * Resets the current AI generation session.
 * Use this when starting a completely new form generation.
 */
export function resetSession() {
  currentSessionId = null;
}

async function callEdgeFunction(prompt: string, currentForm?: GeneratedForm): Promise<GeneratedForm> {
  const sessionId = getOrGenerateSessionId();
  
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const { data, error } = await supabase.functions.invoke('ai-generate-form', {
      body: { prompt, current_form: currentForm, sessionId },
    });

    if (error) {
      const message = error.message || 'Unknown error';

      // Check for lock collision (409)
      if (message.includes('429') || message.includes('RATE_LIMIT') || message.includes('409') || message.includes('ALREADY_PROCESSING')) {
        if (attempt < MAX_RETRIES) {
          const delay = message.includes('409') ? 1500 : BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[AI Service] Busy or limited, retrying in ${delay}ms...`);
          await sleep(delay);
          continue;
        }
      }
      throw new Error(message);
    }

    // Our edge function returns { success: true, data: ... }
    if (!data?.success) {
      const errMsg = data?.error || 'Generation failed';

      // Server-side rate limit response
      if (
        errMsg.includes('Rate limit') ||
        errMsg.includes('quota') ||
        errMsg.includes('429')
      ) {
        if (attempt < MAX_RETRIES) {
          const delay = BASE_RETRY_DELAY_MS * Math.pow(2, attempt);
          console.warn(`[AI Service] Server rate limit, retrying in ${delay}ms (attempt ${attempt + 1}/${MAX_RETRIES})`);
          await sleep(delay);
          continue;
        }
      }
      throw new Error(errMsg);
    }

    const result = data.data;
    if (!result || typeof result !== 'object') {
      throw new Error('AI returned an invalid response structure.');
    }
    
    const form = result as GeneratedForm;
    if (!form.title) {
       throw new Error('AI generation failed: Missing form title.');
    }
    if (!Array.isArray(form.fields)) {
       throw new Error('AI generation failed: Form fields are missing or invalid.');
    }
    
    return form;
  }

  throw new Error('Max retries exceeded. Please try again in a moment.');
}

async function processQueue() {
  if (isProcessing || requestQueue.length === 0) return;
  isProcessing = true;

  while (requestQueue.length > 0) {
    const item = requestQueue.shift()!;

    // Enforce minimum interval between requests
    const elapsed = Date.now() - lastRequestTime;
    if (elapsed < MIN_REQUEST_INTERVAL_MS) {
      await sleep(MIN_REQUEST_INTERVAL_MS - elapsed);
    }

    try {
      // Check cache first
      const cached = getCached(item.intent);
      if (cached) {
        item.resolve(cached);
        continue;
      }

      lastRequestTime = Date.now();
      const result = await callEdgeFunction(item.intent, item.currentForm);
      if (!item.currentForm) {
        setCache(item.intent, result);
      }
      item.resolve(result);
    } catch (err: any) {
      console.error('[AI Service] Generation failed:', err.message);
      item.reject(err);
    }
  }

  isProcessing = false;
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Generate a form from a natural language prompt.
 * Requests are queued, deduplicated, cached, and rate-limited.
 */
export function generateForm(intent: string, currentForm?: GeneratedForm): Promise<GeneratedForm> {
  const trimmed = intent.trim();
  if (!trimmed) return Promise.reject(new Error('Intent is required'));
  if (trimmed.length > 500) return Promise.reject(new Error('Intent must be under 500 characters'));

  const normalized = normalizePrompt(trimmed);
  const lockKey = normalized + (currentForm ? JSON.stringify(currentForm) : '');

  // 1. Check if same request is already executing (In-Flight)
  const inFlight = inFlightRequests.get(lockKey);
  if (inFlight) {
    console.log('[AI Service] Piggybacking on in-flight request for:', normalized);
    return inFlight;
  }

  // 2. Check cache (only if not iterating)
  if (!currentForm) {
    const cached = getCached(trimmed);
    if (cached) return Promise.resolve(cached);
  }

  // 3. Check queue
  const existing = requestQueue.find(
    (q) => normalizePrompt(q.intent) === normalized && !q.currentForm && !currentForm
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      const orig = existing;
      const origResolve = orig.resolve;
      const origReject = orig.reject;
      orig.resolve = (v) => { origResolve(v); resolve(v); };
      orig.reject = (e) => { origReject(e); reject(e); };
    });
  }

  const promise = new Promise<GeneratedForm>((resolve, reject) => {
    requestQueue.push({ intent: trimmed, currentForm, resolve, reject });
    processQueue();
  });

  // Track this promise as in-flight
  inFlightRequests.set(lockKey, promise);
  
  // Cleanup in-flight on settle
  promise.finally(() => {
    inFlightRequests.delete(lockKey);
  });

  return promise;
}

/**
 * Cancel any pending generation requests.
 */
export function cancelGeneration() {
  requestQueue.length = 0;
  if (activeAbortController) {
    activeAbortController.abort();
    activeAbortController = null;
  }
}

/**
 * Clear the prompt cache (e.g., on logout).
 */
export function clearCache() {
  promptCache.clear();
}

/**
 * Create a debounced version of a function.
 */
export function createDebounce<T extends (...args: any[]) => any>(
  fn: T,
  delayMs: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
}
